import { Children } from 'react';

import { BRAINTREE_SCRIPT_VERSION, STRIPE_SCRIPT_VERSION } from '../../constants/PaymentConstants';
import BaseComponent from '../BaseComponent';
import LoadingComponent from '../LoadingComponent';
import ScriptLoader, {
  ScriptLoaderProps,
} from './ScriptLoader';
import connect, { DefaultConnectProps } from '../../utils/connect';

interface HelperProps extends DefaultConnectProps, ScriptLoaderProps {
  children?: React.ReactNode;
  className?: string;
  loadBraintree?: boolean;
  loadKount?: boolean;
  loadStripe?: boolean;
  deviceData?: string;
  nonBlocking?: boolean;
}

class PaymentScriptLoaderHelper extends BaseComponent<HelperProps> {
  componentDidMount() {
    if (this.props.scriptsLoaded && !this.props.scriptsLoadSuccess) {
      this.handleScriptError();
    }
  }

  componentDidUpdate(prevProps: HelperProps) {
    const { actions, loadBraintree, loadKount, deviceData, scriptsLoaded, scriptsLoadSuccess } =
      this.props;
    if (!scriptsLoaded) {
      return;
    }
    if (!prevProps.scriptsLoaded && !scriptsLoadSuccess) {
      this.handleScriptError();
    }
    if (
      scriptsLoadSuccess &&
      !prevProps.scriptsLoadSuccess &&
      loadBraintree &&
      loadKount &&
      !deviceData
    ) {
      actions.paymentCard.collectDeviceData();
    }
  }

  handleScriptError() {
    window.LOGGER?.error('Failed to load Stripe and Braintree scripts', { level: 'critical' });
    this.props.actions.toast.showError(
      'We are unable to process credit card payments. Remove any ad/privacy blockers and refresh the page.'
    );
  }

  render() {
    const { className, scriptsLoaded, nonBlocking } = this.props;
    const isLoading = !scriptsLoaded;

    return (
      <LoadingComponent
        containerClassName={className}
        isLoading={isLoading}
        showOverlay={nonBlocking}
        isBallPulse={true}
      >
        {Children.toArray(this.props.children)}
      </LoadingComponent>
    );
  }
}

interface Props {
  children?: React.ReactNode;
  className?: string;
  loadBraintree?: boolean;
  loadKount?: boolean;
  loadStripe?: boolean;
  stripePublicKey?: string;
  deviceData?: string;
  nonBlocking?: boolean;
}

class PaymentScriptLoader extends BaseComponent<Props & DefaultConnectProps> {
  static defaultProps = {
    loadBraintree: true,
    loadChasePay: true,
    loadKount: true,
    loadStripe: true,
  };

  initializeStripe() {
    if (BROWSER && window.Stripe && !window.stripeV3 && this.props.stripePublicKey) {
      window.stripeV3 = window.Stripe(this.props.stripePublicKey);
    }
  }

  renderContent = (injectProps: ScriptLoaderProps) => {
    this.initializeStripe();
    return (
      <PaymentScriptLoaderHelper {...this.props} {...injectProps}>
        {Children.toArray(this.props.children)}
      </PaymentScriptLoaderHelper>
    );
  };

  render() {
    if (
      typeof window !== 'undefined' &&
      window.DO_NOT_LOAD_PAYMENT_SCRIPTS_BECAUSE_WE_ARE_IN_TEST
    ) {
      return <>{this.props.children}</>;
    }

    const { loadBraintree, loadKount, loadStripe } = this.props;
    const scripts: { [id: string]: string } = {};

    if (loadStripe) {
      scripts.stripeJS = `https://js.stripe.com/${STRIPE_SCRIPT_VERSION}/`;
    }
    if (loadBraintree) {
      scripts.braintreeJS = `https://js.braintreegateway.com/web/${BRAINTREE_SCRIPT_VERSION}/js/client.min.js`;
      scripts.braintreeHostedFieldsJS = `https://js.braintreegateway.com/web/${BRAINTREE_SCRIPT_VERSION}/js/hosted-fields.min.js`;
      scripts.braintreeApplePayJS = `https://js.braintreegateway.com/web/${BRAINTREE_SCRIPT_VERSION}/js/apple-pay.min.js`;
    }
    if (loadKount) {
      scripts.deviceDataJS = `https://js.braintreegateway.com/web/${BRAINTREE_SCRIPT_VERSION}/js/data-collector.min.js`;
    }

    return <ScriptLoader scripts={scripts} retryAttempts={2} render={this.renderContent} />;
  }
}

export default connect<Props>(PaymentScriptLoader);
